/**
 * Cost Center Mapping Form Page Component
 * /admin/cost-center-mappings/new and /admin/cost-center-mappings/:id/edit
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  TextField,
  Grid,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import apiClient from '../api/client';

const CostCenterMappingForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [customers, setCustomers] = useState([]);
  // Sourced from the server rather than hardcoded here; see loadDepartments.
  const [departments, setDepartments] = useState([]);

  const [formData, setFormData] = useState({
    customer: '',
    cost_center_code: '',
    department: '',
    description: '',
    is_active: true,
  });

  useEffect(() => {
    loadCustomers();
    loadDepartments();
    if (isEdit) {
      loadMapping();
    }
  }, [id]);

  // Departments come from the server's own field metadata. The list used to be
  // written out here as operations/sales/management/other while the model
  // accepted DD/MWD/RNM/SDL/SPERRY-MWD - no value in common - so saving a
  // mapping with a department chosen was rejected every time, and an existing
  // record's department could not even be displayed. Keeping one copy, on the
  // server, is what stops that recurring.
  const loadDepartments = async () => {
    try {
      const choices = await apiClient.getFieldChoices(
        '/bookings/cost-center-mappings/',
        'department'
      );
      setDepartments(choices);
    } catch (err) {
      // The field is optional, so an unreachable metadata endpoint should not
      // block the rest of the form. An empty list renders as "None only",
      // which is honest about what can be offered.
      console.error('Failed to load department choices:', err);
      setDepartments([]);
    }
  };

  const loadCustomers = async () => {
    try {
      const response = await apiClient.getCustomers({ is_active: true });
      const data = Array.isArray(response) ? response : (response.results || response.data || []);
      setCustomers(data);
    } catch (err) {
      console.error('Failed to load customers:', err);
      setError('Failed to load customers');
    }
  };

  const loadMapping = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await apiClient.getCostCenterMapping(id);
      
      setFormData({
        customer: data.customer || '',
        cost_center_code: data.cost_center_code || '',
        department: data.department || '',
        description: data.description || '',
        is_active: data.is_active !== undefined ? data.is_active : true,
      });
    } catch (err) {
      setError(err.message || 'Failed to load cost center mapping');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      setSuccessMessage('');

      const submitData = {
        customer: formData.customer,
        cost_center_code: formData.cost_center_code,
        is_active: formData.is_active,
      };

      if (formData.department) submitData.department = formData.department;
      if (formData.description) submitData.description = formData.description;

      let response;
      if (isEdit) {
        response = await apiClient.updateCostCenterMapping(id, submitData);
        setSuccessMessage('Cost center mapping updated successfully!');
      } else {
        response = await apiClient.createCostCenterMapping(submitData);
        setSuccessMessage('Cost center mapping created successfully!');
      }

      // Navigate to mapping detail page after a short delay
      setTimeout(() => {
        navigate(`/admin/cost-center-mappings/${response.id || id}`);
      }, 1500);
    } catch (err) {
      setError(err.message || `Failed to ${isEdit ? 'update' : 'create'} cost center mapping`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/cost-center-mappings')}
            sx={{ mb: 1 }}
          >
            Back to Cost Center Mappings
          </Button>
          <Typography variant="h4" component="h1">
            {isEdit ? 'Edit Cost Center Mapping' : 'New Cost Center Mapping'}
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Customer */}
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth required>
                <InputLabel>Customer</InputLabel>
                <Select
                  name="customer"
                  value={formData.customer}
                  onChange={handleChange}
                  label="Customer"
                >
                  <MenuItem value="">Select Customer</MenuItem>
                  {customers.map((customer) => (
                    <MenuItem key={customer.id} value={customer.id}>
                      {customer.customer_name || customer.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {customers.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  No customers available. Please create customers first.
                </Typography>
              )}
            </Grid>

            {/* Cost Center Code */}
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                name="cost_center_code"
                label="Cost Center Code"
                value={formData.cost_center_code}
                onChange={handleChange}
                required
                helperText="Unique code for this cost center (per customer)"
              />
            </Grid>

            {/* Department */}
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Department</InputLabel>
                <Select
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  label="Department"
                >
                  <MenuItem value="">None</MenuItem>
                  {departments.map((choice) => (
                    <MenuItem key={choice.value} value={choice.value}>
                      {choice.display_name || choice.value}
                    </MenuItem>
                  ))}
                  {/* A stored value the server no longer offers would otherwise
                      vanish from the control, and saving would silently clear
                      it. Keep it selectable and marked. */}
                  {formData.department &&
                    !departments.some((c) => c.value === formData.department) && (
                      <MenuItem value={formData.department}>
                        {formData.department} (no longer offered)
                      </MenuItem>
                    )}
                </Select>
              </FormControl>
            </Grid>

            {/* Active Status */}
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  name="is_active"
                  value={formData.is_active}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.value === 'true' }))}
                  label="Status"
                >
                  <MenuItem value={true}>Active</MenuItem>
                  <MenuItem value={false}>Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Description */}
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                name="description"
                label="Description"
                multiline
                rows={3}
                value={formData.description}
                onChange={handleChange}
                helperText="Optional description for this cost center"
              />
            </Grid>

            {/* Submit Button */}
            <Grid size={{ xs: 12 }}>
              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/admin/cost-center-mappings')}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : isEdit ? 'Update Mapping' : 'Create Mapping'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default CostCenterMappingForm;

